<template>
  <div class="LocatieInformatie__Content">
    <div
      v-if="loading"
      class="d-flex align-items-center justify-content-center my-3"
    >
      <i
        class="far fa-3x fa-spinner fa-spin"
        style="--fa-animation-duration: 1.5s;"
      />
    </div>
    <div
      v-else
      class="d-flex mb-3"
    >
      <div class="d-flex flex-column w-50 position-relative mr-2">
        <strong>{{ $t('components.sidebar.LocatieInformatieContent.top') }}</strong>
        <span v-if="!locationImages.top">
          {{ $t('components.sidebar.LocatieInformatieContent.noImage') }}
        </span>
        <span
          v-else
          class="thumbnail"
        >
          <img
            :src="locationImages.top"
            alt="top image"
          >
        </span>
      </div>
      <div class="d-flex flex-column w-50 position-relative">
        <strong>{{ $t('components.sidebar.LocatieInformatieContent.side') }}</strong>
        <span v-if="!locationImages.side">
          {{ $t('components.sidebar.LocatieInformatieContent.noImage') }}
        </span>
        <span
          v-else
          class="thumbnail"
        >
          <img
            :src="locationImages.side"
            alt="side image"
          >
        </span>
      </div>
    </div>
    <p v-if="remark">
      <strong>{{ $t('remark') }}</strong><br>
      {{ remark }}
    </p>
    <template v-if="address">
      <p>
        <strong>
          {{ $t('address.title') }} <ButtonCopyValue :value="address.formatted_address || ''" />
        </strong>

        <br>

        {{ address.simple_address }} <br>
        {{ address.postalcode }} {{ address.city }}
      </p>

      <p v-if="address.source">
        <strong>{{ $t('components.sidebar.LocatieInformatieContent.addressSource') }}</strong> <br>
        {{ addressSource }}
      </p>
      <div
        v-if="superuser && voltInfo"
        class="d-flex"
      >
        <p>
          <strong>Volt ID</strong><br>
          {{ voltInfo.id }}
        </p>
        <p class="ml-3">
          <strong>Volt status</strong><br>
          {{ voltInfo.status }}
        </p>
        <p class="ml-3">
          <strong>{{ $t('createdAt') }} (Volt):</strong><br>
          {{ voltInfo.created_on }}
        </p>
      </div>

      <p v-if="evse">
        <strong>{{ $t('evse_location_name') }}</strong><br>
        {{ evse }}
      </p>

      <p v-if="evse_ids.length">
        <strong>{{ $t('evse_ids') }}</strong><br>
        {{ evse_ids.join(', ') }}
      </p>

      <p v-if="coordinates.lat && coordinates.lng">
        <strong>
          {{ $t('address.coordinates') }} <SvgIcon
            v-if="importedWithoutCoordinates"
            id="no-coordinates-hint"
            icon="info-circle-regular"
          />
        </strong> <br>
        {{ coordinates.lat }}, <br>
        {{ coordinates.lng }}
      </p>
      <b-tooltip
        v-if="importedWithoutCoordinates"
        target="no-coordinates-hint"
        triggers="hover"
      >
        {{ $t('components.sidebar.LocatieInformatieContent.coordatesTooltip') }}
      </b-tooltip>
    </template>
    <p v-else>
      <HintAddressIncomplete />

      <strong>{{ $t('address.title') }}</strong><br>
      Onbekend
    </p>

    <p v-if="status">
      <strong>{{ $t('mapLayersPanel.status.title') }}</strong><br>
      {{ $t(`participationPanel.options.statusStations.regularLabels.${statusLabel}`) }}
    </p>

    <div class="the-validators">
      <p v-if="status.includes('private') && publish_allowed_to?.length > 0">
        <strong> Netwerk</strong><br>
        <ul>
          <li
            v-for="network in publish_allowed_to"
            :key="network.uuid"
          >
            {{ network.name }}
          </li>
        </ul>
      </p>
    </div>
    <p>
      <strong>{{ $t('cpo') }}</strong><br>
      {{ currentCpo ? currentCpo.name : $t('none') }}
    </p>

    <p v-if="owner">
      <strong>{{ $t('components.sidebar.LocatieInformatieContent.owner') }}</strong><br>
      {{ owner.name }}
    </p>

    <p v-if="parkinglotIds">
      <strong>{{ $t('mapLayersPanel.municipality.parkingSpacesAmsterdam.title') }}</strong><br>
      {{ parkinglotIds }}
    </p>

    <div v-if="prio && prio.order">
      <strong>{{ $t('components.sidebar.PlanModeSearch.priority.label') }}</strong>
      <p>{{ prio.order }}</p>
    </div>

    <div v-if="hasPhase">
      <strong>{{ $t('phase') }}</strong>
      <p>{{ phase }}</p>
    </div>

    <div v-if="trafficDecision">
      <strong>{{ $t(`${translationsPath}.trafficDecision`) }}</strong>
      <p
        v-for="(date, key) in trafficDecision"
        :key="key"
      >
        <span class="d-block">{{ $t(`${translationsPath}.${key}`) }}</span>
        {{ formatDate(date) }}
      </p>
    </div>

    <p v-if="introducedBy">
      <strong>{{ $t('introducedBy') }}</strong><br>
      {{ introducedBy }}
    </p>

    <p v-if="power">
      <strong>{{ $t('components.sidebar.PlanModeSearch.power') }}</strong><br>
      max. {{ power | formatTokW }}
    </p>

    <p v-if="nroutlets">
      <strong>{{ $t('numChargers') }}</strong><br>
      {{ nroutlets }}
    </p>

    <p v-if="types">
      <strong>{{ $t('components.sidebar.LocatieInformatieContent.connectorTypes') }}</strong><br>
      <span
        v-for="type of types"
        :key="type"
      >
        {{ type }}<br></span>
    </p>

    <AlternativeLocation :predecessor="predecessor" />

    <p>
      <strong>{{ $t('components.sidebar.LocatieInformatieContent.locationManagement') }}</strong>
    </p>

    <p v-if="closingDateParticipation">
      <strong>
        {{
          $t(
            'components.sidebar.LocatieInformatieContent.closingDateParticipation'
          )
        }} </strong><br>
      {{ reversedDateParticipation }}
    </p>

    <p>
      <strong>{{ $t('administrator') }}</strong><br>
      {{ beheerder ? beheerder.name : $t('none') }}
    </p>

    <div class="the-validators">
      <p class="mb-0">
        <strong>{{ $t('validators') }}</strong>
      </p>

      <ul v-if="hasValidators">
        <li
          v-for="validator in validators"
          :key="validator.user_id"
        >
          {{ validator.name }}
        </li>
      </ul>

      <span v-else>
        {{ $t('none') }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { reverseDate } from '@/helpers/date'
import { phaseToArray, phaseLabelByUuid } from '@/helpers/phases'
import chargingpointDataMixin from '@/mixins/chargingpoint/chargingpointDataMixin'
import chargingpointEditMixin from '@/mixins/chargingpoint/chargingpointEditMixin'
import { getFileByUUID } from '@/../shared/API/images'
import SvgIcon from '@/components/common/SvgIcon'

import ButtonCopyValue from '@/components/common/ButtonCopyValue'
import HintAddressIncomplete from '@/components/map/sidebar/HintAddressIncomplete'
import AlternativeLocation from '@/components/map/sidebar/AlternativeLocation'

export default {
  components: { AlternativeLocation, HintAddressIncomplete, SvgIcon, ButtonCopyValue },
  mixins: [chargingpointDataMixin, chargingpointEditMixin],
  data () {
    return {
      translationsPath: 'components.sidebar.LocatieInformatieContent',
      locationImages: { top: null, side: null },
      loading: false,
    }
  },
  computed: {
    ...mapGetters('config', {
      config: 'config',
      participationConfigs: 'participationConfigs',
      getPhases: 'getPhases',
    }),
    ...mapGetters('access', ['getPrivateNetworks']),
    phaseKey () {
      return this.prio?.fase || this.prio?.phase
    },
    hasPhase() {
      return phaseToArray(this.prio?.customPhase).length || phaseToArray(this.phaseKey).length
    },
    phase() {
      const phases = phaseToArray(this.phaseKey)
      const customPhases = phaseToArray(this.prio?.customPhase)
      const selectedPhases = [
        ...phases.map(phase => `Fase ${phaseLabelByUuid(this.getPhases, phase)}`),
        ...customPhases.map(phase => phaseLabelByUuid(this.getPhases, phase)),
      ]

      return selectedPhases.length ? selectedPhases.join(', ') : this.$t('none')
    },
    superuser() {
      return this.$auth.user && this.$auth.user['https://evmaps.nl/superuser']
    },
    voltInfo () {
      const metaImport = this.chargingpoint.meta?.import
      return metaImport?.voltID
        ? {
            id: metaImport.voltID,
            status: metaImport.status_nl,
            created_on: new  Date(metaImport.created_on).toLocaleDateString('nl', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
          }
        : null
    },
    voltStatus () {
      return this.chargingpoint.meta?.import?.status_nl
    },
    parkinglotIds() {
      return this.chargingpoint.properties.parkinglotIds?.join(', ')
    },
    closingDatesStation() {
      return this.chargingpoint.participation?.closingDates || []
    },
    closingDateParticipation() {
      return this.closingDatesStation.at(-1)
    },
    reversedDateParticipation() {
      return reverseDate(this.closingDateParticipation)
    },
  },

  watch: {
    chargingpoint: {
      immediate: true,
      deep: true,
      handler() {
        this.locationImages.top = null
        this.locationImages.side = null
        this.loadImages()
      },
    },
  },

  methods: {
    formatDate (date) {
      return new Date(date).toLocaleDateString(this.$i18n.locale)
    },
    async loadImages() {
      this.loading = true
      const primaryTopImage = this.chargingpoint?.images?.top?.find(image => image.primary)
      const primarySideImage = this.chargingpoint?.images?.side?.find(image => image.primary)
      if (primaryTopImage) {
        try {
          const top = await getFileByUUID(primaryTopImage.endpoint, primaryTopImage.index)
          this.locationImages.top = top
        } catch (err) {
          console.error(err)
        }
      }
      if (primarySideImage) {
        try {
          const side = await getFileByUUID(primarySideImage.endpoint, primarySideImage.index)
          this.locationImages.side = side
        } catch (err) {
          console.error(err)
        }
      }
      this.loading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.thumbnail {
  position: relative;
  width: 100%;
  padding-top: 100%;
  cursor: pointer;
  border-radius: 6px;
  border: 2px solid transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s;
  }
}
</style>
